// Admin patrons — list + refund action.
//
// Same page handles both Founding Patrons and Pledges, filtered by type.
// Per the patron program plan, amounts are admin-visible here (the
// public Founders Wall never shows them).
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface PatronRow {
  id: string;
  user_id: string | null;
  contribution_type: "founding_patron" | "pledge";
  amount_usd: number;
  amount_original: number;
  currency_original: string;
  external_payment_id: string;
  status: string;
  recognition_name: string | null;
  show_publicly: boolean;
  founder_number: number | null;
  message: string | null;
  refund_window_ends_at: string | null;
  refunded_at: string | null;
  refund_reason: string | null;
  contributed_at: string;
  profiles: { username: string; avatar_url: string | null } | null;
}

interface ListResponse {
  contributions: PatronRow[];
  total: number;
}

const TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "founding_patron", label: "Founding Patrons" },
  { value: "pledge", label: "Pledges" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "refunded", label: "Refunded" },
  { value: "failed", label: "Failed" },
  { value: "disputed", label: "Disputed" },
];

function formatUsd(n: number | null): string {
  if (n == null) return "—";
  return `$${Number(n).toFixed(2)}`;
}
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
function statusColor(s: string): string {
  switch (s) {
    case "completed":
      return "var(--upvote, #22C55E)";
    case "pending":
      return "var(--accent)";
    case "failed":
    case "disputed":
      return "var(--danger, #ef4444)";
    case "refunded":
      return "var(--text-secondary)";
    default:
      return "var(--text-primary)";
  }
}

export default function AdminPatronsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [refundTarget, setRefundTarget] = useState<PatronRow | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set("type", typeFilter);
      if (statusFilter) params.set("status", statusFilter);
      params.set("limit", "100");
      const res = await api<ListResponse>(
        `/patrons/admin/contributions?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function submitRefund() {
    if (!refundTarget) return;
    setRefundSubmitting(true);
    setRefundError(null);
    try {
      await api(`/patrons/contributions/${refundTarget.id}/refund`, {
        method: "POST",
        body: { reason: refundReason.trim() },
      });
      setRefundTarget(null);
      setRefundReason("");
      await fetchList();
    } catch (err) {
      setRefundError(err instanceof Error ? err.message : "Refund failed");
    } finally {
      setRefundSubmitting(false);
    }
  }

  const rows = data?.contributions ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Patron contributions</h1>
        <CardSubtle>
          Founding Patrons (capped cohort, $5K+) and Pledges (any amount, no cap).
          Refunds fire Stripe refunds; the webhook handles the DB state
          transition and reopens the program if a refund drops the count
          below the cap.
        </CardSubtle>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Type</Label>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No contributions</CardTitle>
          <CardSubtle>Nothing matches the current filters.</CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>#</Th>
                <Th>Type</Th>
                <Th>Contributor</Th>
                <Th>Recognition</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>When</Th>
                <Th>Refund window</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={styles.tableRow}>
                  <Td>
                    {row.founder_number ? (
                      <strong>#{row.founder_number}</strong>
                    ) : (
                      <CardSubtle style={{ fontSize: 12 }}>—</CardSubtle>
                    )}
                  </Td>
                  <Td>
                    {row.contribution_type === "founding_patron"
                      ? "Founding"
                      : "Pledge"}
                  </Td>
                  <Td>
                    {row.profiles?.username
                      ? `@${row.profiles.username}`
                      : "(unknown)"}
                  </Td>
                  <Td>
                    {row.show_publicly ? (
                      <span>
                        <strong>{row.recognition_name ?? "(anon)"}</strong>
                        {row.message && (
                          <CardSubtle style={{ fontSize: 12, fontStyle: "italic" }}>
                            "{row.message}"
                          </CardSubtle>
                        )}
                      </span>
                    ) : (
                      <CardSubtle style={{ fontSize: 12 }}>private</CardSubtle>
                    )}
                  </Td>
                  <Td>
                    <strong>{formatUsd(row.amount_usd)}</strong>
                    {row.currency_original !== "USD" && (
                      <CardSubtle style={{ fontSize: 12 }}>
                        {row.amount_original} {row.currency_original}
                      </CardSubtle>
                    )}
                  </Td>
                  <Td>
                    <span style={{ color: statusColor(row.status) }}>
                      {row.status}
                    </span>
                    {row.refund_reason && (
                      <CardSubtle
                        style={{ fontSize: 12, fontStyle: "italic" }}
                      >
                        {row.refund_reason}
                      </CardSubtle>
                    )}
                  </Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(row.contributed_at)}
                    </CardSubtle>
                  </Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(row.refund_window_ends_at)}
                    </CardSubtle>
                  </Td>
                  <Td>
                    {row.status === "completed" && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setRefundTarget(row)}
                      >
                        Refund
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={refundTarget !== null}
        onClose={() => !refundSubmitting && setRefundTarget(null)}
        title={
          refundTarget
            ? `Refund ${formatUsd(refundTarget.amount_usd)} ${refundTarget.contribution_type === "founding_patron" ? "Founding Patron" : "Pledge"} from @${refundTarget.profiles?.username ?? "?"}`
            : ""
        }
      >
        <CardSubtle style={{ marginBottom: 12 }}>
          Fires a Stripe refund. The charge.refunded webhook reverses the
          state and clears the founder number (if applicable). If this
          drops the count below the cap, the Founding Patron program is
          automatically reopened.
        </CardSubtle>
        <Label>Reason</Label>
        <Textarea
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value)}
          rows={3}
          placeholder="Briefly explain why this is being refunded"
        />
        {refundError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>
            {refundError}
          </div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setRefundTarget(null)}
            disabled={refundSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitRefund}
            disabled={refundReason.trim().length < 3 || refundSubmitting}
          >
            {refundSubmitting ? "Refunding…" : "Refund"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  tableHead: { background: "rgba(255,255,255,0.02)", textAlign: "left" },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
};
